import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from './Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog'

interface ConfirmDialogProps {
  /** The control that opens it; it is cloned with an onClick. */
  trigger: React.ReactElement<{ onClick?: () => void }>
  title: string
  description?: string
  confirmLabel?: string
  onConfirm: () => void | Promise<void>
}

/**
 * The one place an irreversible action is confirmed.
 *
 * It exists because the alternative was `window.confirm`, which ignores the
 * app's theme and language and reads as a browser warning rather than as part
 * of the product.
 */
export default function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [working, setWorking] = useState(false)

  const confirm = async () => {
    setWorking(true)
    try {
      await onConfirm()
      setOpen(false)
    } finally {
      setWorking(false)
    }
  }

  return (
    <>
      {/* Radix's own Trigger would need every caller's button to forward refs. */}
      <span onClick={() => setOpen(true)}>{trigger}</span>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={working}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirm} loading={working}>
              {confirmLabel ?? t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
